import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/shared/FormField';
import { toast } from '@/lib/swal';
import { usersService } from '@/services/users.service';
import { inspectionService } from '@/services/inspection.service';

interface TransferDialogProps {
  inspectionId: string;
  currentOwnerId: string;
  open: boolean;
  onClose: () => void;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

export function TransferDialog({
  inspectionId,
  currentOwnerId,
  open,
  onClose,
}: TransferDialogProps) {
  const qc = useQueryClient();
  const [toEngineerId, setToEngineerId] = useState('');
  const [reason, setReason] = useState('');

  const { data: users = [] } = useQuery({
    queryKey: ['users-list'],
    queryFn: () => usersService.listAll(),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () => inspectionService.initiateTransfer(inspectionId, { toEngineerId, reason }),
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      setToEngineerId('');
      setReason('');
      onClose();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to initiate transfer')),
  });

  const canSubmit = toEngineerId && reason.trim().length >= 10 && !mutation.isPending;

  return (
    <Modal open={open} onClose={onClose} title="Transfer Inspection">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Transfer ownership of this inspection to another engineer. They must accept before the
          transfer takes effect.
        </p>

        <FormField label="Transfer To" htmlFor="transfer-to" required>
          <select
            id="transfer-to"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            value={toEngineerId}
            onChange={(e) => setToEngineerId(e.target.value)}
          >
            <option value="">— Select engineer —</option>
            {users
              .filter((u) => u.id !== currentOwnerId)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName}
                </option>
              ))}
          </select>
        </FormField>

        <FormField label="Reason" htmlFor="transfer-reason" required>
          <Textarea
            id="transfer-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why you are transferring this inspection..."
            rows={4}
          />
          <span className="text-xs text-muted-foreground">
            {reason.length} / 1000 · minimum 10 characters
          </span>
        </FormField>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!canSubmit}>
            {mutation.isPending ? 'Submitting…' : 'Request Transfer'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
